'use client'

import { cn } from '@/lib/utils'
import {
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices
} from '@/types'
import { Icon, Img } from '@/ui/components'
import { MEDIA } from '@spot'
import useEmblaCarousel from 'embla-carousel-react'
import { memo, useCallback, useEffect, useState } from 'react'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data:
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanitySport
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityEventList
}

const DetailSlide = ({ data }: Props) => {
  const item = data?.content?.item

  const [emblaRef, emblaApi] = useEmblaCarousel()
  //states
  const [disabledNext, setDisabledNext] = useState<boolean>(false)
  const [disabledPrev, setDisabledPrev] = useState<boolean>(false)

  //stores

  const checkDisabled = useCallback(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.canScrollPrev() ? setDisabledPrev(false) : setDisabledPrev(true)
    emblaApi.canScrollNext() ? setDisabledNext(false) : setDisabledNext(true)
  }, [emblaApi])

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', checkDisabled)
      emblaApi.on('init', checkDisabled)
    }
  }, [emblaApi, checkDisabled])

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  if (item?.images) {
    return item.images.length === 1 ? (
      <div>
        <Img
          crop={{ width: 920, height: 524 }}
          data={item.images[0]}
          fill
          objectFit
          className="h-[32.75rem] w-full max-sm:h-[21.5rem] md:h-[26.25rem]"
          sizes={`${MEDIA.tablet} 100vw, 57.5rem`}
          quality={80}
        />
        <p
          className={cn(
            'text-small',
            'p-md px-gutter min-h-9 text-neutral-400'
          )}
        >
          {item.images[0].properties?.description ||
            item.images[0].properties?.title}
        </p>
      </div>
    ) : (
      <div className={cn('embla', 'relative')} ref={emblaRef}>
        <div className="grid auto-cols-[100%] grid-flow-col">
          {item.images.length > 1 &&
            item?.images.map((img, index) => (
              <div key={index?.toString()}>
                <Img
                  data={img}
                  fill
                  objectFit
                  className="h-131 w-full max-sm:h-86 md:h-105"
                  sizes={`${MEDIA.tablet} 100vw, 57.5rem`}
                  quality={80}
                />
                <p
                  className={cn(
                    'text-small',
                    'p-md px-gutter min-h-9 text-neutral-400'
                  )}
                >
                  {img.properties?.description || img.properties?.title}
                </p>
              </div>
            ))}
        </div>
        <div className="absolute top-[29.75rem] right-0 z-[5] grid grid-cols-2 backdrop-blur-[12px] md:top-auto md:bottom-[2.2rem]">
          <button
            className="flex h-12 w-12 cursor-pointer flex-col items-center justify-center bg-neutral-100 transition-[background-color,opacity] duration-[var(--duration-default)] ease-[var(--ease-out-cubic)] hover:bg-neutral-200 data-[disable=true]:pointer-events-none data-[disable=true]:bg-transparent data-[disable=true]:opacity-70 hover:[&_.icon-arrow]:scale-110"
            onClick={scrollPrev}
            data-disable={disabledPrev}
          >
            <Icon
              className="icon-arrow h-5 w-5 transition-transform duration-[var(--duration-default)] ease-[var(--ease-out-cubic)]"
              icon="material-symbols:arrow-back-ios-new"
            />
          </button>
          <button
            data-disable={disabledNext}
            className="flex h-12 w-12 cursor-pointer flex-col items-center justify-center bg-neutral-100 transition-[background-color,opacity] duration-[var(--duration-default)] ease-[var(--ease-out-cubic)] hover:bg-neutral-200 data-[disable=true]:pointer-events-none data-[disable=true]:bg-transparent data-[disable=true]:opacity-70 hover:[&_.icon-arrow]:scale-110"
            onClick={scrollNext}
            data-disabled={emblaApi?.canScrollPrev()}
          >
            <Icon
              className="icon-arrow h-5 w-5 transition-transform duration-[var(--duration-default)] ease-[var(--ease-out-cubic)]"
              icon="material-symbols:arrow-forward-ios"
            />
          </button>
        </div>
      </div>
    )
  }
}

export default memo(DetailSlide)
